import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { QuoteNode } from '@synoptic/lexical-nodes/rich-text.js';
import { $getRoot, $isElementNode, LexicalNode } from 'lexical';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { hasTextSelection } from '@/utils/has-text-selection';
import { collapsedQuote } from './plugin.css';

type CollapsibleQuotesPluginProps = {
  forceExpand?: boolean;
  collapsedLines?: number;
  collapseThresholdChars?: number;
};

const DEFAULT_COLLAPSE_THRESHOLD_CHARS = 100;
const ICON_RESERVED_PADDING_PX = 28;

type QuoteMeta = { key: string; overflowing: boolean };

const $collectQuotes = (
  node: LexicalNode,
  thresholdChars: number,
  into: QuoteMeta[],
): void => {
  if (node instanceof QuoteNode) {
    const text = node.getTextContent();
    into.push({
      key: node.getKey(),
      overflowing: text.length > thresholdChars,
    });
    return;
  }
  if ($isElementNode(node)) {
    for (const child of node.getChildren()) {
      $collectQuotes(child, thresholdChars, into);
    }
  }
};

const clearCollapsedStyles = (el: HTMLElement) => {
  el.classList.remove(collapsedQuote);
  el.style.display = '';
  el.style.webkitBoxOrient = '';
  el.style.webkitLineClamp = '';
  el.style.overflow = '';
  el.style.cursor = '';
  el.style.paddingRight = '';
  el.style.paddingBottom = '';
  el.style.height = '';
  el.style.maxHeight = '';
  el.style.boxSizing = '';
};

const measureLineHeightPx = (el: HTMLElement): number => {
  const cs = window.getComputedStyle(el);
  const raw = parseFloat(cs.lineHeight);
  if (Number.isFinite(raw) && raw >= 4) return raw;
  const fs = parseFloat(cs.fontSize) || 14;
  const multiplier = Number.isFinite(raw) && raw > 0 ? raw : 1.3;
  return fs * multiplier;
};

const COLLAPSED_HEIGHT_PADDING_PX = 3;

const applyCollapsedStyles = (el: HTMLElement, lines: number) => {
  const lh = measureLineHeightPx(el);
  const cs = window.getComputedStyle(el);
  const pt = parseFloat(cs.paddingTop) || 0;
  const totalHeight = Math.floor(lh * lines + pt) + COLLAPSED_HEIGHT_PADDING_PX;

  el.classList.add(collapsedQuote);
  el.style.display = '-webkit-box';
  el.style.webkitBoxOrient = 'vertical';
  el.style.webkitLineClamp = String(lines);
  el.style.overflow = 'hidden';
  el.style.boxSizing = 'border-box';
  el.style.height = `${totalHeight}px`;
  el.style.maxHeight = `${totalHeight}px`;
  el.style.cursor = 'pointer';
  el.style.paddingRight = `${ICON_RESERVED_PADDING_PX}px`;
  el.style.paddingBottom = '0';
};

const areArraysEqual = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((v, i) => v === b[i]);

const areBoolArraysEqual = (
  a: readonly boolean[],
  b: readonly boolean[],
): boolean => a.length === b.length && a.every((v, i) => v === b[i]);

const areSetsEqual = (
  a: ReadonlySet<number>,
  b: ReadonlySet<number>,
): boolean => {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
};

export const CollapsibleQuotesPlugin = ({
  forceExpand,
  collapsedLines = 2,
  collapseThresholdChars = DEFAULT_COLLAPSE_THRESHOLD_CHARS,
}: CollapsibleQuotesPluginProps) => {
  const [editor] = useLexicalComposerContext();
  const [keys, setKeys] = useState<readonly string[]>([]);
  const [overflowing, setOverflowing] = useState<readonly boolean[]>([]);
  const [expandedIndices, setExpandedIndices] = useState<ReadonlySet<number>>(
    () => new Set(),
  );
  const [prevKeysLength, setPrevKeysLength] = useState(keys.length);

  if (keys.length !== prevKeysLength) {
    setPrevKeysLength(keys.length);
    if (expandedIndices.size > 0) {
      const trimmed = new Set<number>();
      expandedIndices.forEach((idx) => {
        if (idx < keys.length) trimmed.add(idx);
      });
      if (!areSetsEqual(expandedIndices, trimmed)) {
        setExpandedIndices(trimmed);
      }
    }
  }

  const overflowingRef = useRef(overflowing);
  overflowingRef.current = overflowing;
  const forceExpandRef = useRef(forceExpand);
  forceExpandRef.current = forceExpand;

  useEffect(() => {
    const commit = () => {
      const metas: QuoteMeta[] = [];
      editor.read(() =>
        $collectQuotes($getRoot(), collapseThresholdChars, metas),
      );
      const nextKeys = metas.map((m) => m.key);
      const nextOverflowing = metas.map((m) => m.overflowing);
      setKeys((prev) => (areArraysEqual(prev, nextKeys) ? prev : nextKeys));
      setOverflowing((prev) =>
        areBoolArraysEqual(prev, nextOverflowing) ? prev : nextOverflowing,
      );
    };
    commit();
    return editor.registerMutationListener(QuoteNode, commit);
  }, [editor, collapseThresholdChars]);

  useEffect(() => {
    const apply = () => {
      keys.forEach((key, idx) => {
        const el = editor.getElementByKey(key);
        if (!el) return;
        const isOverflowing = overflowing[idx];
        const shouldCollapse =
          isOverflowing && !forceExpand && !expandedIndices.has(idx);
        if (shouldCollapse) {
          applyCollapsedStyles(el, collapsedLines);
        } else {
          clearCollapsedStyles(el);
          if (isOverflowing && !forceExpand) {
            el.style.cursor = 'pointer';
          }
        }
      });
    };
    apply();
    return editor.registerUpdateListener(apply);
  }, [editor, keys, overflowing, expandedIndices, forceExpand, collapsedLines]);

  useLayoutEffect(
    () => () => {
      const rootEl = editor.getRootElement();
      if (!rootEl) return;
      rootEl.querySelectorAll('blockquote').forEach((el) => {
        clearCollapsedStyles(el as HTMLElement);
      });
    },
    [editor],
  );

  useEffect(() => {
    const cleanups: Array<() => void> = [];
    keys.forEach((key, idx) => {
      const el = editor.getElementByKey(key);
      if (!el) return;
      const handler = (e: Event) => {
        if (forceExpandRef.current) return;
        if (!overflowingRef.current[idx]) return;
        if (hasTextSelection()) return;
        e.stopPropagation();
        setExpandedIndices((prev) => {
          const next = new Set(prev);
          if (next.has(idx)) next.delete(idx);
          else next.add(idx);
          return next;
        });
      };
      el.addEventListener('click', handler);
      cleanups.push(() => el.removeEventListener('click', handler));
    });
    return () => cleanups.forEach((c) => c());
  }, [editor, keys]);

  return null;
};
